import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { mapYingshiDevicesToMergedList } from '@/views/ys/mergeAccountAndTrustDevices';
import { formatDateTimeStr } from '@/utils/renderUtils';
import { formatSurveillanceAlertDuration } from '@/views/surveillance/components/SurveillanceAlertOverlay';
import {
  getSeeData,
  getStreamKey,
  getEventTimeMs,
  getMonitoringSceneFlags,
  getMonitoringAlertSummary,
  getMonitoringThumbnailUrl,
  getMonitoringFullImageUrl,
  buildMonitoringStreamKeyFromDevice
} from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils';
import {
  getMonitoringAlarmSseList,
  getMonitoringAlarmProcessedStreamKeysMap,
  getDepartureSseByStreamKey
} from '@/redux/sse/sseSlice';

function departureStatusText(rec, nursingLabelByValue) {
  if (!rec || typeof rec !== 'object' || Array.isArray(rec)) return '';
  const sub = rec.subType == null ? '' : String(rec.subType).trim();
  const label = (sub && nursingLabelByValue && nursingLabelByValue.get(sub)) || sub;
  const nurseName = rec.nurseName == null ? '' : String(rec.nurseName).trim();
  if (label && nurseName) return `${label}（${nurseName}带走）`;
  return label;
}

export function babyMonitorDevices(yingshiDeviceList, storeId) {
  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';
  if (!selectedStoreId) return [];
  if (String(yingshiDeviceList?.query?.storeId ?? '') !== selectedStoreId) return [];
  return mapYingshiDevicesToMergedList(yingshiDeviceList?.data).filter(
    (d) => d.needInference === true
  );
}

export function readBabyMonitorView(device, sse, nursingLabelByValue) {
  const wantKey = buildMonitoringStreamKeyFromDevice(device.deviceSerial, device.channelNo);
  const processed = Boolean(getMonitoringAlarmProcessedStreamKeysMap(sse)[wantKey]);
  let sseSeeData = null;
  let sseTimeMs = 0;
  const sseItems = getMonitoringAlarmSseList(sse);
  for (let i = 0; i < sseItems.length; i += 1) {
    const item = sseItems[i];
    if (getStreamKey(item) !== wantKey) continue;
    const seeData = getSeeData(item);
    const t = getEventTimeMs(seeData);
    if (!sseSeeData || t >= sseTimeMs) {
      sseSeeData = seeData;
      sseTimeMs = t;
    }
  }

  const flags = sseSeeData && !processed
    ? getMonitoringSceneFlags(sseSeeData)
    : { hasBaby: device.hasBaby, isViolation: undefined };
  const hasBaby = flags.hasBaby;
  const isViolation = flags.isViolation;
  const isAlarming = device.isAlarming === true;
  const warning = hasBaby === false
    || isViolation === true
    || (!sseSeeData && isAlarming);
  const sseSummary = sseSeeData && !processed ? getMonitoringAlertSummary(sseSeeData) : '';
  const listSummary = device.alarmSummary != null ? String(device.alarmSummary).trim() : '';
  const warningText = sseSummary
    || listSummary
    || (hasBaby === false ? '无婴儿看护，请登记宝宝去向' : '监控预警');

  const sseImage = sseSeeData
    ? (getMonitoringFullImageUrl(sseSeeData) || getMonitoringThumbnailUrl(sseSeeData))
    : '';
  const screenUrl = sseImage
    || (device.latestMonitoringImage || '').trim()
    || (device.latestMonitoringImageThumbnail || '').trim();
  const eventTimeMs = sseTimeMs > 0 ? sseTimeMs : (Number(device.alarmTimeMs) || 0);

  const bedNo = device.bedNo == null ? '' : String(device.bedNo).trim();
  const room = (device.location && String(device.location).trim()) || device.name || '--';
  const online = device.status === 1 || device.status === '1';

  let dest = '';
  const sseDepEvt = getDepartureSseByStreamKey(sse)[wantKey];
  const sseBed = sseDepEvt
    ? String(sseDepEvt.payload && sseDepEvt.payload.status != null ? sseDepEvt.payload.status : '')
      .trim()
      .toLowerCase()
      .replace(/-/g, '_')
    : '';
  const sseLeft = sseBed === 'pre_departed'
    || sseBed === 'active'
    || sseBed === 'departed'
    || sseBed === 'return_awaiting';
  if (sseDepEvt && (sseDepEvt.eventType === 'departure_return' || sseBed === 'return_pending')) {
    dest = '送回待确认';
  } else if (sseDepEvt && sseLeft) {
    const rec = sseDepEvt.payload && sseDepEvt.payload.departure;
    dest = departureStatusText(rec, nursingLabelByValue);
  } else if (!sseDepEvt && device.isAlarming === false) {
    const bed = String(device.bedStatus == null ? '' : device.bedStatus)
      .trim()
      .toLowerCase()
      .replace(/-/g, '_');
    if (bed === 'return_pending') {
      dest = '送回待确认';
    } else if (bed && bed !== 'in_place' && bed !== 'completed') {
      dest = departureStatusText(device.latestDepartureRecord, nursingLabelByValue);
    }
  }

  return {
    key: wantKey || `${device.deviceSerial}_${device.channelNo}`,
    deviceSerial: device.deviceSerial,
    channelNo: device.channelNo,
    bedNo,
    room,
    online,
    screenUrl,
    warning,
    warningText,
    hasBaby,
    dest,
    statusTail: dest || (warning ? warningText : '正常看护'),
    eventTimeMs
  };
}

function BabyMonitor({ storeId }) {
  const yingshiDeviceList = useSelector((state) => state.yingshi.deviceList);
  const sse = useSelector((state) => state.sse);
  const dictOptions = useSelector((state) => state.components.dictOptions);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';

  const devices = useMemo(
    () => babyMonitorDevices(yingshiDeviceList, selectedStoreId),
    [yingshiDeviceList, selectedStoreId]
  );

  const nursingLabelByValue = useMemo(() => {
    const list = Array.isArray(dictOptions?.nursing_type?.data)
      ? dictOptions.nursing_type.data
      : [];
    const map = new Map();
    for (let i = 0; i < list.length; i += 1) {
      const item = list[i];
      if (!item || item.value == null || item.value === '') continue;
      const raw = item.label != null ? item.label : (item.desc != null ? item.desc : item.value);
      map.set(String(item.value), String(raw == null ? '' : raw).trim());
    }
    return map;
  }, [dictOptions]);

  const alertViews = useMemo(() => {
    const views = devices
      .map((device) => readBabyMonitorView(device, sse, nursingLabelByValue))
      .filter((view) => view.warning);
    views.sort((a, b) => (b.eventTimeMs || 0) - (a.eventTimeMs || 0));
    return views.slice(0, 5);
  }, [devices, sse, nursingLabelByValue]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [selectedStoreId]);

  useEffect(() => {
    if (alertViews.length === 0) {
      setCurrentIndex(0);
      return undefined;
    }
    setCurrentIndex((i) => i % alertViews.length);
    return undefined;
  }, [alertViews.length]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (alertViews.length <= 1 || paused) return undefined;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % alertViews.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [alertViews.length, paused, currentIndex]);

  const fetching = yingshiDeviceList?.fetching === true;
  const listReady = String(yingshiDeviceList?.query?.storeId ?? '') === selectedStoreId;

  if (!selectedStoreId) {
    return (
      <div className="baby-monitor">
        <div className="bm-empty">请选择门店</div>
      </div>
    );
  }

  if (!listReady && fetching) {
    return (
      <div className="baby-monitor">
        <div className="bm-empty">加载中...</div>
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div className="baby-monitor">
        <div className="bm-empty">本店暂无开启婴儿监控的摄像头</div>
      </div>
    );
  }

  if (alertViews.length === 0) {
    return (
      <div className="baby-monitor">
        <div className="bm-empty">暂无告警画面</div>
      </div>
    );
  }

  const current = alertViews[currentIndex] || alertViews[0];
  const {
    warning,
    warningText,
    hasBaby,
    screenUrl,
    eventTimeMs,
    bedNo: bed,
    room,
    online,
    statusTail
  } = current;
  const durationMs = eventTimeMs > 0 ? Math.max(0, nowMs - eventTimeMs) : 0;

  const nowDate = new Date(nowMs);
  const timeText = nowDate.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const detectText = hasBaby === false ? '【检测】无婴儿' : (hasBaby === true ? '【检测】有婴儿' : '');

  return (
    <div
      className="baby-monitor"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className={`bm-warning${warning ? '' : ' bm-warning-placeholder'}`}>
        {warning ? (
          <>
            <div className="bm-warning-left">
              <div className="bm-warning-icon">⚠</div>
              <span className="bm-warning-text">{warningText}</span>
            </div>
            <div className="bm-warning-right">
              <div className="bm-issue-row">
                <span className="bm-issue-label">发出</span>
                <span className="bm-issue-time">
                  {eventTimeMs > 0 ? formatDateTimeStr(eventTimeMs) : '--'}
                </span>
              </div>
              <div className="bm-duration-value">{formatSurveillanceAlertDuration(durationMs)}</div>
            </div>
          </>
        ) : (
          <div className="bm-warning-left">
            <span className="bm-warning-text bm-warning-ok">正常看护</span>
          </div>
        )}
      </div>

      <div className="bm-screen">
        <div
          className="bm-screen-bg"
          style={screenUrl ? { backgroundImage: `url(${screenUrl})` } : undefined}
        />
        <div className="bm-screen-overlay">
          <div className="bm-camera-id">{current.deviceSerial || '--'}</div>
          <div className="bm-time">{timeText}</div>
        </div>
        {detectText ? (
          <div className="bm-detection-labels">
            <div className="bm-detection-label">{detectText}</div>
          </div>
        ) : null}
        <div className="bm-screen-inner">
          <div className="bm-bed-label">床位</div>
          <div className="bm-bed-number">{bed || '--'}</div>
        </div>
        <div className="bm-screen-corner-br">{bed ? `${bed}号床` : room}</div>
        <div className="bm-screen-footer">
          <span className="bm-resolution">1080P</span>
        </div>
        {alertViews.length > 1 ? (
          <>
            <button
              type="button"
              className="bm-arrow bm-arrow-left"
              onClick={() => setCurrentIndex((i) => (i - 1 + alertViews.length) % alertViews.length)}
            >
              ‹
            </button>
            <button
              type="button"
              className="bm-arrow bm-arrow-right"
              onClick={() => setCurrentIndex((i) => (i + 1) % alertViews.length)}
            >
              ›
            </button>
          </>
        ) : null}
      </div>

      <div className="bm-status-bar">
        <div className="bm-status-item">
          <span className={`bm-status-icon${online ? ' online' : ''}`}>●</span>
          <span className="bm-status-text">{online ? '在线' : '离线'}</span>
        </div>
        <div className="bm-status-item">
          <span className="bm-status-icon">📍</span>
          <span className="bm-status-text">{room}</span>
        </div>
        <div className="bm-status-item">
          <span className="bm-status-icon">📡</span>
          <span className="bm-status-text">{`通道 ${current.channelNo ?? '--'}`}</span>
        </div>
        <div className={`bm-status-item${warning ? ' bm-status-warn' : ''}`}>
          <span className={`bm-status-icon${warning ? ' warn' : ' online'}`}>⚠</span>
          <span className="bm-status-text">{statusTail}</span>
        </div>
      </div>

      <div className="bm-alert-thumbs">
        {alertViews.map((view, idx) => (
          <button
            type="button"
            key={view.key}
            className={`bm-alert-thumb${idx === currentIndex ? ' active' : ''}`}
            style={view.screenUrl ? { backgroundImage: `url(${view.screenUrl})` } : undefined}
            onClick={() => setCurrentIndex(idx)}
            title={view.bedNo ? `${view.bedNo}号床` : view.warningText}
          >
            <span className="bm-alert-thumb-bed">{view.bedNo || '--'}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default BabyMonitor;
